import { Link } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForgotPassword } from "@/hooks/use-forgot-password";
import { forgotPasswordSchema, type ForgotPasswordRequest } from "@/api/forgot-password";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const ForgotPasswordPage = () => {
  const { mutateAsync: forgotPassword, isPending: isLoading } =
    useForgotPassword();

  const form = useForm<ForgotPasswordRequest>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (data: ForgotPasswordRequest) => {
    await forgotPassword(data);
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background">
      <div className="p-12 w-full max-w-[440px] min-w-[350px] shadow-xl space-y-6 rounded-xl">
        <img src="/logo.svg" alt="bernardino logo" width={300} className="mx-auto"/>
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            Recuperar contraseña
          </h1>
          <p className="text-sm text-muted-foreground">
            Ingrese su correo electrónico para recuperar su contraseña
          </p>
        </div>

        <div className="grid gap-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Correo electrónico</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        autoCapitalize="none"
                        autoComplete="email"
                        autoCorrect="off"
                        autoFocus={true}
                        disabled={isLoading}
                        placeholder="nombre@ejemplo.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  "Recuperar contraseña"
                )}
              </Button>
            </form>
          </Form>
        </div>

        <div className="flex justify-center">
          <Link
            to="/inicia-sesion"
            className="text-sm text-muted-foreground hover:underline underline-offset-4 hover:text-primary"
          >
            Volver a iniciar sesión
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
